import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { CheckCircle2, ShieldCheck } from "lucide-react";
import { FIRMA, LOGO_URL, DISCLAIMER } from "@/lib/branding";
import { docLabel, INVOICE_TYPES } from "@/lib/constants";
import { buildGiroCodePayload, buildSwissQrPayload, computeAmounts, documentPositions, manualBillingLines, fmtCurrency, fmtDate, fmtDateTime, positionLineNet, positionQuantity, effectiveBank, isGiroCodeEligible, isSwissQrEligible, taxHintForCountry } from "@/lib/quote-helpers";

const Card = ({ children, testid }) => (
  <div className="bg-white rounded-xl border border-[var(--brand-border)] shadow-sm p-4" data-testid={testid}>
    {children}
  </div>
);

const SectionLabel = ({ children }) => (
  <div className="text-[11px] uppercase tracking-wider mb-2 font-semibold" style={{ color: "var(--brand-primary)" }}>
    {children}
  </div>
);

export const MobileQuoteView = ({ quote, company = {}, steuer = {}, total = 0, signature = {} }) => {
  const isInvoice = INVOICE_TYPES.includes(quote.dokument_typ);
  const taxInfo = taxHintForCountry(quote.kunde_land, company, quote.currency);
  const eb = effectiveBank(steuer, quote);
  const a = computeAmounts(quote);
  const savedPositions = Array.isArray(quote.positions) ? quote.positions : [];
  const hasManualBilling = manualBillingLines(quote).length > 0;
  const hasPositions = savedPositions.length > 0 || hasManualBilling;
  const positions = hasPositions ? documentPositions(quote) : [];
  const textKey = (value) => String(value || "").replace(/\s+/g, " ").trim().toLocaleLowerCase("de-DE");
  const generalTitleKey = textKey(quote.leistung_titel);
  const generalDescriptionKey = textKey(quote.leistung_beschreibung);
  const titleAlreadyInPosition = !!generalTitleKey && savedPositions.some((position) => textKey(position.titel) === generalTitleKey);
  const descriptionAlreadyInPosition = !!generalDescriptionKey && savedPositions.some((position) =>
    (position.bullets || []).some((detail) => textKey(detail) === generalDescriptionKey),
  );
  const showGeneralTitle = !!generalTitleKey && !titleAlreadyInPosition;
  const showGeneralDescription = !!generalDescriptionKey && !descriptionAlreadyInPosition;
  const showGeneralServiceBlock = hasPositions && !hasManualBilling && (showGeneralTitle || showGeneralDescription);
  const anySelected = hasPositions && positions.some((p) => p.include_in_total);
  const showTotal = !hasPositions || anySelected;
  const intro = isInvoice
    ? "vielen Dank für Ihren Auftrag. Nachfolgend erhalten Sie unsere Rechnung über die erbrachte Leistung."
    : (quote.einleitung && quote.einleitung.trim()) ||
      `vielen Dank für Ihre Anfrage. Nachfolgend erhalten Sie ${docLabel(quote.dokument_typ, company)}.`;

  return (
    <div className="space-y-4" data-testid="mobile-quote-view">
      {/* Kopf */}
      <Card testid="mobile-quote-header">
        <div className="flex items-center gap-3 mb-3">
          {LOGO_URL ? (
            <img src={LOGO_URL} alt="Logo" className="h-10 w-10 rounded-lg object-contain flex-shrink-0" />
          ) : (
            <div
              className="h-10 w-10 rounded-lg flex items-center justify-center text-white font-bold text-lg flex-shrink-0"
              style={{ background: "var(--brand-ink)" }}
            >
              {(FIRMA.firma || FIRMA.name || "A").charAt(0)}
            </div>
          )}
          <div className="min-w-0">
            <div className="font-semibold text-lg leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
              {docLabel(quote.dokument_typ, company)}
            </div>
            <div className="text-xs text-[var(--brand-muted)] truncate">{FIRMA.firma}</div>
          </div>
        </div>
        <div className="text-sm space-y-1.5">
          <div className="flex justify-between gap-3">
            <span className="text-[var(--brand-muted)]">{isInvoice ? "Rechnungsnummer" : `${docLabel(quote.dokument_typ, company)}nummer`}</span>
            <span className="font-medium">{quote.angebot_nr || "—"}</span>
          </div>
          {quote.ref_doc_nr && (
            <div className="flex justify-between gap-3">
              <span className="text-[var(--brand-muted)]">Bezug</span>
              <span className="font-medium">{quote.ref_doc_nr}</span>
            </div>
          )}
          <div className="flex justify-between gap-3">
            <span className="text-[var(--brand-muted)]">Datum</span>
            <span className="font-medium">{fmtDate(quote.angebot_datum)}</span>
          </div>
          <div className="flex justify-between gap-3">
            <span className="text-[var(--brand-muted)]">{isInvoice ? "Fällig bis" : "Gültig bis"}</span>
            <span className="font-medium">{fmtDate(quote.gueltig_bis)}</span>
          </div>
          {quote.leistungsdatum && (
            <div className="flex justify-between gap-3">
              <span className="text-[var(--brand-muted)]">Leistungsdatum</span>
              <span className="font-medium">{quote.leistungsdatum}</span>
            </div>
          )}
        </div>
      </Card>

      {/* Parteien */}
      <Card testid="mobile-quote-parties">
        <SectionLabel>{isInvoice ? "Rechnungssteller" : "Auftragnehmer"}</SectionLabel>
        <div className="text-sm leading-relaxed mb-4">
          <div className="font-semibold">{FIRMA.name}</div>
          <div>{FIRMA.firma}</div>
          <div>{FIRMA.strasse}</div>
          <div>{FIRMA.plz_ort}</div>
          {FIRMA.telefon && <div className="text-[var(--brand-muted)]">Tel.: {FIRMA.telefon}</div>}
          {FIRMA.email && <div className="text-[var(--brand-muted)] break-all">{FIRMA.email}</div>}
          {steuer.steuernummer && <div className="text-[var(--brand-muted)]">Steuernummer: {steuer.steuernummer}</div>}
          {steuer.ustid && <div className="text-[var(--brand-muted)]">USt-IdNr.: {steuer.ustid}</div>}
        </div>
        <SectionLabel>{isInvoice ? "Rechnungsempfänger" : `${docLabel(quote.dokument_typ, company)}empfänger`}</SectionLabel>
        <div className="text-sm leading-relaxed">
          <div className="font-semibold">
            {quote.anrede} {quote.kunde_name || "—"}
          </div>
          {quote.kunde_firma && <div>{quote.kunde_firma}</div>}
          {quote.kunde_strasse && <div>{quote.kunde_strasse}</div>}
          {quote.kunde_plz_ort && <div>{quote.kunde_plz_ort}</div>}
          {quote.kunde_land && <div>{quote.kunde_land}</div>}
          {quote.kunde_ustid && <div className="text-[var(--brand-muted)]">USt-IdNr.: {quote.kunde_ustid}</div>}
        </div>
      </Card>

      {/* Anschreiben */}
      <Card testid="mobile-quote-intro">
        <p className="text-sm mb-2">
          Sehr geehrte{quote.anrede === "Herr" ? "r" : ""} {quote.anrede} {quote.kunde_name || "Musterkunde"},
        </p>
        <p className="text-sm leading-relaxed whitespace-pre-line text-[var(--brand-ink)]">{intro}</p>
      </Card>

      {/* Leistungen */}
      <Card testid="mobile-quote-positions">
        <SectionLabel>Leistungsumfang</SectionLabel>
        {showGeneralServiceBlock && (
          <div className="mb-4 rounded-lg border border-[var(--brand-border)] bg-[var(--brand-bg)] p-3" data-testid="mobile-general-service-description">
            {showGeneralTitle && <div className="font-semibold text-sm text-[var(--brand-ink)]">{quote.leistung_titel}</div>}
            {showGeneralDescription && <p className={`${showGeneralTitle ? "mt-1 " : ""}text-sm leading-relaxed whitespace-pre-line text-[var(--brand-ink)]`}>{quote.leistung_beschreibung}</p>}
          </div>
        )}
        {hasPositions ? (
          <div className="space-y-3">
            {positions.map((pos, idx) => {
              const isIn = !!pos.include_in_total;
              const details = (pos.bullets || []).filter((b) => b && b.trim());
              return (
                <div
                  key={pos.id || idx}
                  className="rounded-lg border overflow-hidden"
                  style={{ borderColor: isIn ? "var(--brand-primary)" : "var(--brand-border)", borderWidth: isIn ? 2 : 1 }}
                >
                  <div className="px-3 py-2" style={{ background: isIn ? "var(--brand-border-light)" : "var(--brand-bg)" }}>
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-semibold text-sm leading-snug">
                        {isIn && <span style={{ color: "var(--brand-primary)" }}>✓ </span>}
                        {idx + 1}. {pos.titel}
                      </span>
                    </div>
                  </div>
                  {details.length > 0 && (
                    <ul className="px-3 py-2 text-sm space-y-1 bg-white">
                      {details.map((b, i) => (
                          <li key={i} className="flex items-start gap-2 leading-relaxed">
                            <span className="inline-block mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: "var(--brand-primary)" }} />
                            <span>{b}</span>
                          </li>
                      ))}
                    </ul>
                  )}
                  <div className="grid grid-cols-3 gap-2 border-t border-[var(--brand-border)] bg-white px-3 py-2 text-xs">
                    <div><div className="text-[var(--brand-muted)]">Menge</div><div className="mt-0.5 font-medium">{positionQuantity(pos)} {pos.einheit || ""}</div></div>
                    <div><div className="text-[var(--brand-muted)]">Einzelpreis</div><div className="mt-0.5 font-medium">{fmtCurrency(pos.preis, a.currency)}</div></div>
                    <div className="text-right"><div className="text-[var(--brand-muted)]">Gesamt netto</div><div className="mt-0.5 font-semibold">{fmtCurrency(positionLineNet(pos), a.currency)}</div></div>
                  </div>
                  {!isIn && <div className="border-t border-[var(--brand-border)] bg-white px-3 pb-2 text-[10px] italic text-[var(--brand-muted)]">Optionale Position – nicht im Gesamtbetrag eingerechnet</div>}
                </div>
              );
            })}
            {!positions.some((p) => p.include_in_total) && (
              <p className="text-xs text-[var(--brand-muted)] italic">
                Bitte gewünschte Pakete auswählen – nur ausgewählte Positionen fließen in den Gesamtbetrag ein.
              </p>
            )}
          </div>
        ) : (
          <div className="text-sm">
            <div className="font-semibold">{quote.leistung_titel}</div>
            {quote.leistung_beschreibung && (
              <p className="text-xs text-[var(--brand-muted)] mt-1 leading-relaxed whitespace-pre-line">{quote.leistung_beschreibung}</p>
            )}
            <div className="flex justify-between mt-3 pt-2 border-t border-[var(--brand-border)]">
              <span className="text-[var(--brand-muted)]">
                {quote.anzahl_bewertungen} × {fmtCurrency(quote.preis_pro_bewertung)}
              </span>
              <span className="font-semibold">{fmtCurrency(total)}</span>
            </div>
          </div>
        )}
      </Card>

      {/* Summen */}
      {showTotal && (
        <Card testid="mobile-quote-totals">
          <div className="text-sm space-y-2">
            <div className="flex justify-between">
              <span className="text-[var(--brand-muted)]">Nettobetrag</span>
              <span>{fmtCurrency(a.net, a.currency)}</span>
            </div>
            {a.tax > 0 ? (
              <div className="flex justify-between text-[var(--brand-muted)]">
                <span>zzgl. MwSt {quote.tax_rate}%</span>
                <span>{fmtCurrency(a.tax, a.currency)}</span>
              </div>
            ) : (
              <p className="text-xs text-[var(--brand-muted)]" data-testid="mobile-tax-note">
                {steuer?.tax_status === "kleinunternehmer"
                  ? "Gemäß § 19 UStG wird keine Umsatzsteuer berechnet."
                  : taxInfo.short}
              </p>
            )}
          </div>
          <div
            className="flex justify-between items-center rounded-lg px-4 py-3 mt-3"
            style={{ background: "var(--brand-primary)", color: "white" }}
          >
            <span className="font-semibold">Gesamtbetrag</span>
            <span className="font-bold text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>
              {fmtCurrency(a.gross, a.currency)}
            </span>
          </div>
          {a.deposit > 0 && (
            <div className="text-sm mt-3 space-y-1.5">
              <div className="flex justify-between" style={{ color: "var(--brand-primary)" }}>
                <span className="font-medium">Anzahlung fällig</span>
                <span className="font-semibold">{fmtCurrency(a.deposit, a.currency)}</span>
              </div>
              <div className="flex justify-between text-[var(--brand-muted)]">
                <span>Restbetrag</span>
                <span>{fmtCurrency(a.rest, a.currency)}</span>
              </div>
            </div>
          )}
          {a.prepaid > 0 && (
            <div className="text-sm mt-3 space-y-1.5">
              <div className="flex justify-between text-[var(--brand-muted)]">
                <span>abzgl. erhaltene Anzahlung</span>
                <span>− {fmtCurrency(a.prepaid, a.currency)}</span>
              </div>
              <div className="flex justify-between font-semibold" style={{ color: "var(--brand-primary)" }}>
                <span>Noch zu zahlen</span>
                <span>{fmtCurrency(a.due, a.currency)}</span>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* USt-Hinweis (Ausland) */}
      {taxInfo.kind !== "national" && (
        <div
          className="rounded-xl p-4 text-sm leading-relaxed"
          style={{ background: "var(--brand-bg)", borderLeft: "3px solid var(--brand-primary)" }}
          data-testid="mobile-tax-hint"
        >
          <div className="font-semibold mb-1" style={{ color: "var(--brand-primary)" }}>
            {taxInfo.title}
          </div>
          <p>{taxInfo.long}</p>
        </div>
      )}

      {/* Disclaimer */}
      {DISCLAIMER && DISCLAIMER.trim() && (
        <div
          className="rounded-xl p-4"
          style={{ background: "var(--brand-bg)", borderLeft: "3px solid var(--brand-primary)" }}
          data-testid="mobile-disclaimer"
        >
          <div className="text-[11px] uppercase tracking-wider mb-1 font-semibold" style={{ color: "var(--brand-primary)" }}>
            Wichtiger Hinweis
          </div>
          <p className="text-sm leading-relaxed" style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic" }}>
            {DISCLAIMER}
          </p>
        </div>
      )}

      {/* Zahlung */}
      <Card testid="mobile-quote-payment">
        <SectionLabel>Zahlungsbedingungen</SectionLabel>
        <div className="text-sm space-y-2">
          <div>
            <span className="text-[var(--brand-muted)]">Zahlungsziel: </span>
            <span className="font-semibold">{quote.zahlungsziel}</span>
          </div>
          {quote.reseller_link && (
            <div>
              <div className="text-[var(--brand-muted)] text-xs">Zahlungslink</div>
              <a href={quote.reseller_link} className="break-all" style={{ color: "var(--brand-primary)" }}>
                {quote.reseller_link}
              </a>
            </div>
          )}
        </div>
        {isInvoice && (eb.iban || eb.bic || eb.bank) && (
          <div className="rounded-lg p-3 mt-3 text-sm space-y-1.5" style={{ background: "var(--brand-bg)" }} data-testid="mobile-bank-block">
            <div className="text-[11px] uppercase tracking-wider font-semibold" style={{ color: "var(--brand-primary)" }}>
              Bankverbindung
            </div>
            {eb.kontoinhaber && (
              <div className="flex justify-between gap-3">
                <span className="text-[var(--brand-muted)]">Kontoinhaber</span>
                <span className="text-right">{eb.kontoinhaber}</span>
              </div>
            )}
            {eb.iban && (
              <div>
                <span className="text-[var(--brand-muted)]">IBAN</span>
                <div className="font-mono text-xs break-all">{eb.iban}</div>
              </div>
            )}
            {eb.bic && (
              <div className="flex justify-between gap-3">
                <span className="text-[var(--brand-muted)]">BIC</span>
                <span className="font-mono text-xs">{eb.bic}</span>
              </div>
            )}
            {eb.bank && (
              <div className="flex justify-between gap-3">
                <span className="text-[var(--brand-muted)]">Bank</span>
                <span className="text-right">{eb.bank}</span>
              </div>
            )}
            {quote.angebot_nr && (
              <div className="flex justify-between gap-3">
                <span className="text-[var(--brand-muted)]">Verwendungszweck</span>
                <span className="text-right">{quote.angebot_nr}</span>
              </div>
            )}
          </div>
        )}
        {isSwissQrEligible(steuer, quote) && (
          <div className="rounded-lg border-2 border-dashed mt-3 p-3 bg-white" style={{ borderColor: "var(--brand-border)" }} data-testid="mobile-swiss-qr-bill">
            <div className="text-[11px] uppercase tracking-wider font-semibold mb-2" style={{ color: "var(--brand-primary)" }}>
              Schweizer Zahlungs-QR
            </div>
            <div className="flex gap-3 items-center">
              <div className="relative shrink-0" style={{ width: 174, height: 174 }}>
                <QRCodeSVG value={buildSwissQrPayload(steuer, quote)} size={174} level="M" marginSize={4} title="Schweizer Zahlungs-QR für die Banking-App" />
                <div className="absolute top-1/2 left-1/2" style={{ transform: "translate(-50%, -50%)", width: 26, height: 26, background: "#000", border: "2px solid #fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <div style={{ position: "relative", width: 16, height: 16 }}>
                    <div style={{ position: "absolute", top: 5, left: 1, width: 14, height: 5, background: "#fff" }} />
                    <div style={{ position: "absolute", top: 1, left: 5, width: 5, height: 14, background: "#fff" }} />
                  </div>
                </div>
              </div>
              <div className="text-xs leading-relaxed">
                In der Banking-App scannen – Betrag, IBAN und Verwendungszweck werden übernommen.
              </div>
            </div>
          </div>
        )}
        {isGiroCodeEligible(steuer, quote) && (
          <div className="rounded-lg border-2 border-dashed mt-3 p-3 bg-white" style={{ borderColor: "var(--brand-border)" }} data-testid="mobile-giro-qr-bill">
            <div className="text-[11px] uppercase tracking-wider font-semibold mb-2" style={{ color: "var(--brand-primary)" }}>
              Zahlungs-QR für Überweisung per Banking-App
            </div>
            <div className="flex gap-3 items-center">
              <div className="shrink-0" style={{ width: 180, height: 180 }}>
                <QRCodeSVG value={buildGiroCodePayload(steuer, quote)} size={180} level="M" marginSize={4} title="Zahlungs-QR für die Banking-App" />
              </div>
              <div className="text-xs leading-relaxed">
                In der Banking-App scannen – Betrag, IBAN und Verwendungszweck werden übernommen.
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Hinweise & Anlagen */}
      {quote.notizen && quote.notizen.trim() && (
        <Card testid="mobile-quote-notes">
          <SectionLabel>Hinweise</SectionLabel>
          <p className="text-sm whitespace-pre-line leading-relaxed">{quote.notizen}</p>
        </Card>
      )}
      {(quote.attachments || []).length > 0 && (
        <div className="flex items-start gap-2 px-1 text-sm text-[var(--brand-muted)]">
          <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: "var(--brand-primary)" }} />
          <span>Anlagen finden Sie im beigefügten PDF-Dokument.</span>
        </div>
      )}

      {/* Gruß */}
      <Card testid="mobile-quote-signature">
        <p className="text-sm mb-1">Mit besten Grüßen</p>
        {signature.exists && signature.data_url && (
          <img src={signature.data_url} alt="Unterschrift" className="my-2" style={{ maxHeight: 60, maxWidth: 200, objectFit: "contain" }} />
        )}
        <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22 }}>{FIRMA.name}</div>
        <div className="text-xs text-[var(--brand-muted)]">{FIRMA.firma}</div>
      </Card>

      {/* Angenommen-Status */}
      {quote.customer_signed_at && quote.customer_signature && (
        <div
          className="rounded-xl p-4 border"
          style={{ borderColor: "var(--brand-primary)", background: "var(--brand-bg)" }}
          data-testid="mobile-customer-signed"
        >
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle2 className="w-5 h-5" style={{ color: "var(--brand-primary)" }} />
            <span className="font-semibold">{docLabel(quote.dokument_typ, company)} angenommen</span>
          </div>
          <div className="text-xs text-[var(--brand-muted)] mb-2">
            {fmtDateTime(quote.customer_signed_at)}
            {quote.customer_signed_name && ` · ${quote.customer_signed_name}`}
          </div>
          <img src={quote.customer_signature} alt="Unterschrift Kunde" style={{ maxHeight: 60, maxWidth: 200, objectFit: "contain" }} />
        </div>
      )}
    </div>
  );
};
